import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import translate from "../../../i18n/translate";
import { intl } from "../../../i18n/provider";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// The WeCodes agent login behind the "Local" Hot gateway. Toppinex place our
// Hot topups with WeCodes; Local places the same one with WeCodes ourselves.
// Which of the two Hot actually uses is chosen on the Operators page.
//
// A WeCodes agent signs in with an ID and a year of birth and nothing else,
// so those two and the url are the whole form.
// Seconds into something an admin can act on. Mirrors _hot_local_humanise in
// celltop.py, which words the same figure on the server side.
const humanise = (seconds) => {
  const total = Number(seconds) || 0;
  if (total <= 0) return "0";
  const days = Math.floor(total / 86400);
  if (days) return `${days}d`;
  const hours = Math.floor((total % 86400) / 3600);
  if (hours) return `${hours}h`;
  return `${Math.max(Math.floor((total % 3600) / 60), 1)}m`;
};

const HotLocalSettings = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stored, setStored] = useState({
    base_url: "",
    document_id: "",
    year_of_birth: "",
    configured: false,
  });
  const [form, setForm] = useState({ base_url: "", document_id: "", year_of_birth: "" });
  const [showUser, setShowUser] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [session, setSession] = useState({ session: false, session_seconds_left: 0 });
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeResult, setCodeResult] = useState(null);

  const applyStored = (data) => {
    const next = {
      base_url: data.base_url || "",
      document_id: data.document_id || "",
      year_of_birth: data.year_of_birth || "",
      configured: Boolean(data.configured),
    };
    setStored(next);
    if (data.session !== undefined) {
      setSession({
        session: Boolean(data.session),
        session_seconds_left: data.session_seconds_left || 0,
      });
    }
    // Every field is filled from what is stored, so what is on screen after a
    // save is what was actually written - the url included, which the server
    // corrects when the portal address is pasted in.
    setForm({
      base_url: next.base_url,
      document_id: next.document_id,
      year_of_birth: next.year_of_birth,
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("hot_local_credentials?operation=get");
      if (res?.data?.status === "success") applyStored(res.data);
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const pending = () => {
    const body = {};
    if (form.base_url.trim() !== stored.base_url) body.base_url = form.base_url.trim();
    if (form.year_of_birth.trim() !== stored.year_of_birth) {
      body.year_of_birth = form.year_of_birth.trim();
    }
    if (form.document_id.trim() !== stored.document_id) {
      body.document_id = form.document_id.trim();
    }
    return body;
  };

  const dirty = Object.keys(pending()).length > 0;

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) return;

    setSaving(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("hot_local_credentials?operation=update", pending());
      if (res?.data?.status === "success") {
        applyStored(res.data);
        setShowUser(false);
        Toast.fire({ title: translate("hotLocalSaved"), icon: "success" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const sendCode = async () => {
    setSending(true);
    setCodeResult(null);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("hot_local_credentials?operation=send_code", {}, {
        timeout: 60000,
      });
      setCodeResult({ ok: res?.data?.status === "success", reason: res?.data?.reason });
    } catch (err) {
      setCodeResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setVerifying(true);
    setCodeResult(null);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("hot_local_credentials?operation=verify",
        { otp: otp.trim() }, { timeout: 60000 });
      const ok = res?.data?.status === "success";
      setCodeResult({ ok, reason: res?.data?.reason });
      if (ok) {
        setOtp("");
        applyStored({ ...stored, ...res.data });
      }
    } catch (err) {
      setCodeResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setVerifying(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("hot_local_credentials?operation=test", {}, { timeout: 60000 });
      const ok = res?.data?.status === "success";
      setTestResult({
        ok,
        // A login can be right and still not be enough to sell: WeCodes
        // refuse an unverified token everywhere past the login, so that is
        // shown as a warning rather than as a pass or a failure.
        warn: ok && res?.data?.verified === false,
        reason: ok && res?.data?.account
          ? `${res.data.reason} — ${res.data.account}`
          : res?.data?.reason,
      });
    } catch (err) {
      setTestResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("hotLocalHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 2 }}>
          <Typography variant="subtitle1">{translate("Login")}</Typography>
          {loading ? (
            <CircularProgress size={18} />
          ) : stored.configured ? (
            <Chip size="small" color="success" label={stored.document_id} />
          ) : (
            <Chip size="small" color="warning" label={translate("not set")} />
          )}
        </Box>

        <Box component="form" onSubmit={save} sx={{ display: "grid", gap: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={translate("URL")}
            value={form.base_url}
            onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
            placeholder="https://api.wecodes.co.il/api"
            autoComplete="off"
          />
          <TextField
            size="small"
            fullWidth
            label={translate("User")}
            value={form.document_id}
            onChange={(e) => setForm((f) => ({ ...f, document_id: e.target.value }))}
            placeholder="123456789"
            type={showUser ? "text" : "password"}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setShowUser((v) => !v)}
                    aria-label="toggle visibility"
                  >
                    {showUser ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            fullWidth
            label={translate("Date of birth")}
            value={form.year_of_birth}
            onChange={(e) => setForm((f) => ({ ...f, year_of_birth: e.target.value }))}
            placeholder="1985"
            helperText={translate("hotLocalDobHint")}
            autoComplete="off"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" disabled={saving || loading || !dirty}>
              {saving ? <CircularProgress size={20} color="inherit" /> : translate("Save")}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 1 }}>
          <Typography variant="subtitle1">{translate("Session")}</Typography>
          {session.session ? (
            <Chip
              size="small"
              color="success"
              label={intl.formatMessage({ id: "expires in" }) + " " +
                     humanise(session.session_seconds_left)}
            />
          ) : (
            <Chip size="small" color="warning" label={translate("not set")} />
          )}
        </Box>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("hotLocalSessionHint")}
        </Typography>

        <Box component="form" onSubmit={verify} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Button variant="outlined" onClick={sendCode} disabled={sending || !stored.configured}>
            {sending ? <CircularProgress size={20} color="inherit" /> : translate("Send code")}
          </Button>
          <TextField
            size="small"
            label={translate("Code")}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            autoComplete="one-time-code"
            sx={{ width: 160 }}
          />
          <Button type="submit" variant="contained" disabled={verifying || !otp.trim()}>
            {verifying ? <CircularProgress size={20} color="inherit" /> : translate("Verify")}
          </Button>
        </Box>
        {codeResult && (
          <Alert severity={codeResult.ok ? "success" : "error"} sx={{ marginTop: 2 }}>
            {codeResult.reason}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {translate("Test login")}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("hotLocalTestHint")}
        </Typography>
        <Button variant="outlined" onClick={test} disabled={testing || loading || !stored.configured}>
          {testing ? <CircularProgress size={20} color="inherit" /> : translate("Test login")}
        </Button>
        {testResult && (
          <Alert
            severity={testResult.ok ? (testResult.warn ? "warning" : "success") : "error"}
            sx={{ marginTop: 2 }}
          >
            {testResult.reason}
            {testResult.warn && <Box sx={{ marginTop: 1 }}>{translate("hotLocalUnverified")}</Box>}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(HotLocalSettings);
